import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Textarea,
  useColorModeValue,
  useToast,
  Icon,
} from '@chakra-ui/react'
import { MdCancel } from 'react-icons/md'
import { useRejectCancellation } from 'queries/cancellation.query'

export function RejectModal({ isOpen, onClose, cancellation }) {
  const [adminNotes, setAdminNotes] = useState('')
  const toast = useToast()
  const rejectMutation = useRejectCancellation()

  const cardBg = useColorModeValue('gray.50', 'navy.700')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')

  if (!cancellation) return null

  const handleReject = async () => {
    if (!adminNotes.trim()) {
      toast({
        title: 'Rejection Reason Required',
        description: 'Please explain why this request is being declined (it will be sent to the customer).',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    try {
      const res = await rejectMutation.mutateAsync({
        id: cancellation.id,
        adminNotes: adminNotes.trim(),
      })

      toast({
        title: 'Cancellation Request Rejected',
        description: res?.message || 'The customer has been notified and the ticket remains active.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      })
      onClose()
      setAdminNotes('')
    } catch (err) {
      toast({
        title: 'Rejection Failed',
        description: err?.response?.data?.message || err.message || 'Error updating request.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} py={4}>
          <HStack spacing={2.5}>
            <Icon as={MdCancel} color="red.500" w="24px" height="24px" />
            <Text fontSize="lg" fontWeight="bold">
              Decline Cancellation Request
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            <Box p={3.5} borderRadius="12px" bg={cardBg} border="1px solid" borderColor={borderColor} fontSize="xs">
              <HStack justify="space-between" mb={1.5}>
                <Text color="gray.500">Order & Ticket:</Text>
                <Text fontWeight="bold">
                  {cancellation.order?.order_number} — #{cancellation.ticket?.ticket_code}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.500">Customer:</Text>
                <Text fontWeight="semibold">{cancellation.customer_name}</Text>
              </HStack>
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }} mb={1.5}>
                Reason for Rejection * (Sent to Customer via Email):
              </Text>
              <Textarea
                required
                placeholder="e.g. The cancellation window for this event closed prior to this submission, or ticket has already been used..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                borderRadius="12px"
                fontSize="xs"
                rows={4}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor} py={3}>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="10px">
            Cancel
          </Button>
          <Button
            colorScheme="red"
            borderRadius="10px"
            onClick={handleReject}
            isLoading={rejectMutation.isPending}
            loadingText="Declining..."
            px={6}
          >
            Decline Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
